import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getArchivedClasses, toggleArchiveClass } from "../api/Classes";
import { Class } from "../types";
import { useAuth } from '../useAuth';
import { useTimezone } from '../hooks/useTimezone';
import { usePageTitle } from '../hooks/usePageTitle';

function ArchivedClasses() {
  usePageTitle('Archived Classes');
  const navigate = useNavigate();
  const { formatDate } = useTimezone();
  const { user } = useAuth();
  
  const [classes, setClasses] = useState<Class[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unarchiving, setUnarchiving] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadArchivedClasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Use user?.id instead of user object to prevent infinite loop

  const loadArchivedClasses = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getArchivedClasses();
      setClasses(data);
    } catch (err: any) {
      console.error('Failed to load archived classes:', err);
      setError('Failed to load archived classes.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (classId: string) => {
    if (!window.confirm('Unarchive this class? It will appear in your active classes list.')) return;
    
    setUnarchiving(classId);
    try {
      await toggleArchiveClass(classId, false);
      // Remove from local list
      setClasses(classes.filter(c => c._id !== classId));
    } catch (err: any) {
      console.error('Failed to unarchive class:', err);
      alert('Failed to unarchive class: ' + (err.response?.data?.error || err.message));
    } finally {
      setUnarchiving(null);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Archived Classes</h2>
          <button className="btn btn-outline-secondary" onClick={() => navigate('/classes')}>
            <i className="bi bi-arrow-left me-2"></i>Back to Classes
          </button>
        </div>
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Archived Classes</h2>
        <button className="btn btn-outline-secondary" onClick={() => navigate('/classes')}>
          <i className="bi bi-arrow-left me-2"></i>Back to Classes
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search archived classes..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="card">
        <div className="card-header bg-secondary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <i className="bi bi-archive me-2"></i>Archived Classes
            </span>
            <span className="small opacity-75">
              {search
                ? `${classes.filter(c => c.classname?.toLowerCase().includes(search.toLowerCase())).length} of ${classes.length}`
                : classes.length} {classes.length === 1 ? 'class' : 'classes'}
            </span>
          </div>
        </div>
        <div className="card-body p-0">
          {classes.length === 0 ? (
            <div className="p-4 text-center text-muted">
              <i className="bi bi-archive display-1 d-block mb-3 opacity-25"></i>
              <p className="mb-0">No archived classes.</p>
              <small>Archive classes from the Edit Class screen to keep a record without cluttering your active list.</small>
            </div>
          ) : classes.filter(c => c.classname?.toLowerCase().includes(search.toLowerCase())).length === 0 ? (
            <div className="p-4 text-center text-muted">
              <p className="mb-0">No archived classes match "{search}".</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Date</th>
                    <th scope="col">CEC</th>
                    <th scope="col">Status</th>
                    <th scope="col">Archived</th>
                    <th scope="col">Reason</th>
                    <th scope="col" style={{ width: '180px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.filter(c => c.classname?.toLowerCase().includes(search.toLowerCase())).map((cls: Class) => (
                    <tr key={cls._id}>
                      <td title={cls.classname} style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {cls.classname}
                        {cls.docpath && <span className="badge bg-info text-white ms-2" title="Has certificate">▣</span>}
                      </td>
                      <td>{formatDate(cls.classdate)}</td>
                      <td>{cls.classLU}</td>
                      <td>
                        {cls.took ? (
                          <span className="badge bg-success">Completed</span>
                        ) : (
                          <span className="badge bg-warning text-dark">Not Completed</span>
                        )}
                      </td>
                      <td className="text-muted small">
                        {cls.archivedDate ? formatDate(cls.archivedDate) : '—'}
                      </td>
                      <td>
                        {cls.archivedReason === 'auto' ? (
                          <span className="badge bg-info" title="Automatically archived">Auto</span>
                        ) : cls.archivedReason === 'manual' ? (
                          <span className="badge bg-secondary" title="Manually archived">Manual</span>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/classes/${cls._id}?returnTo=/archived-classes`)}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleUnarchive(cls._id)}
                            disabled={unarchiving === cls._id}
                          >
                            {unarchiving === cls._id ? 'Unarchiving...' : 'Unarchive'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="alert alert-info mt-4">
        <i className="bi bi-info-circle me-2"></i>
        <strong>About Archived Classes:</strong> Classes are automatically archived if they are dated before your earliest active cycle start date and have not been assigned to any cycle. You can manually unarchive classes if needed.
      </div>
    </div>
  );
}

export default ArchivedClasses;
